import React, { useState, useRef, useEffect, useCallback, CSSProperties } from 'react';
import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } from '@zxing/library';
import {
  X,
  AlertTriangle,
  Zap,
  Camera,
  Keyboard,
  Info,
  User,
  FileText,
  Package,
  PackageCheck,
  PackageX,
  Box,
  Boxes,
  Check,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';

export interface ScannerProps {
  onScan: (code: string) => void;
  onClose: () => void;
  enableTorch?: boolean;
  enableCameraSwitch?: boolean;
  showScanFrame?: boolean;
  title?: string;
  requireProject?: boolean;
  cartonCode?: string;
  projectId?: string | number;
  onCartonScanned?: (carton: any) => void;
  onPanelScanned?: (panel: any) => void;
  onHardwareScanned?: (hardware: any) => void;
  onError?: (error: Error) => void;
  updateCartonStats?: (stats: {
    totalPanels: number;
    receivedPanels: number;
    totalHardware: number;
    receivedHardware: number;
    pendingItems: number;
  }) => void;
  // Add carton info props
  currentCarton?: {
    id: string;
    code: string;
    panels: Array<{ id: string; code: string; is_received: boolean }>;
    hardware: Array<{ id: string; code: string; is_received: boolean; quantity: number }>;
  } | null;
  cartonContents?: {
    panels: Array<{ id: string; code: string; is_received: boolean }>;
    hardware: Array<{ id: string; code: string; is_received: boolean; quantity: number }>;
  };
}

const Scanner: React.FC<ScannerProps> = ({
  onScan,
  onClose,
  enableTorch = true,
  enableCameraSwitch = true,
  showScanFrame = false,
  title = 'Scan Barcode',
  requireProject = true,
  updateCartonStats,
  currentCarton,
  cartonContents
}) => {
  const { selectedProject, detailedProject } = useProject();
  // Use detailed project data if available, fall back to selected project
  const currentProject = detailedProject || selectedProject;

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<MediaDeviceInfo | null>(null);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const [scanCooldown, setScanCooldown] = useState(false);
  const lastNotificationTime = useRef<number>(0);
  const [qrDetected, setQrDetected] = useState(false);
  const [showCameraSelect, setShowCameraSelect] = useState(false);
  const [scannedCarton, setScannedCarton] = useState<string | null>(null);
  const [scannedCodes, setScannedCodes] = useState<Array<{code: string, timestamp: number}>>([]);
  const [lastScannedCode, setLastScannedCode] = useState('');
  const [currentScanText, setCurrentScanText] = useState('');
  const [isReaderActive, setIsReaderActive] = useState(false);
  const [cartonStats, setCartonStats] = useState({
    panel: { total: 0, received: 0, notReceived: 0 },
    hardware: { total: 0, received: 0, notReceived: 0 }
  });
  const [pendingCarton, setPendingCarton] = useState<string | null>(null);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  // Use carton contents from props if available, otherwise use local state
  const [localCartonContents, setLocalCartonContents] = useState<{
    panels: Array<{ id: string; code: string; is_received: boolean }>;
    hardware: Array<{ id: string; code: string; is_received: boolean; quantity: number }>;
  }>({ panels: [], hardware: [] });
  
  // Use props carton contents if available, otherwise use local state
  const effectiveCartonContents = cartonContents || localCartonContents;
  const [showContents, setShowContents] = useState(true);
  const [errorState, setErrorState] = useState<{
    message: string;
    count: number;
    timestamp: number;
    id: string;
  } | null>(null);
  const [successState, setSuccessState] = useState<{
    message: string;
    count: number;
    timestamp: number;
    id: string;
  } | null>(null);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const notificationTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Smart notification helpers
  const showErrorNotification = useCallback((message: string, duration: number = 4000) => {
    const id = `error_${message}`;
    const now = Date.now();
    
    // Debounce notifications - prevent flooding
    if (now - lastNotificationTime.current < 500) {
      console.log('[NOTIFICATION] Debouncing error notification:', message);
      return;
    }
    lastNotificationTime.current = now;
    
    console.log('[NOTIFICATION] Showing error notification:', { message, duration, id });
    
    // Clear existing timeout for this message
    const existingTimeout = notificationTimeouts.current.get(id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    setErrorState(prev => {
      if (prev && prev.id === id) {
        // Same message - increment count and update timestamp
        console.log('[NOTIFICATION] Incrementing count for existing error notification');
        return {
          ...prev,
          count: prev.count + 1,
          timestamp: now
        };
      } else {
        // New message - reset count
        console.log('[NOTIFICATION] Creating new error notification');
        return {
          message,
          count: 1,
          timestamp: now,
          id
        };
      }
    });
    
    // Clear success notification if showing error
    setSuccessState(null);
    
    // Set auto-hide timeout
    console.log('[NOTIFICATION] Setting timeout for error notification:', duration, 'ms');
    const timeout = setTimeout(() => {
      console.log('[NOTIFICATION] Timeout triggered for error notification:', id);
      setErrorState(prev => {
        if (prev?.id === id) {
          console.log('[NOTIFICATION] Clearing error notification');
          notificationTimeouts.current.delete(id);
          return null;
        }
        console.log('[NOTIFICATION] Error notification ID mismatch, keeping:', prev?.id);
        return prev;
      });
    }, duration);
    
    notificationTimeouts.current.set(id, timeout);
  }, []);

  const showSuccessNotification = useCallback((message: string, duration: number = 3000) => {
    const id = `success_${message}`;
    const now = Date.now();
    
    // Debounce notifications - prevent flooding
    if (now - lastNotificationTime.current < 500) {
      console.log('[NOTIFICATION] Debouncing success notification:', message);
      return;
    }
    lastNotificationTime.current = now;
    
    console.log('[NOTIFICATION] Showing success notification:', { message, duration, id });
    
    // Clear existing timeout for this message
    const existingTimeout = notificationTimeouts.current.get(id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    setSuccessState(prev => {
      if (prev && prev.id === id) {
        // Same message - increment count and update timestamp
        console.log('[NOTIFICATION] Incrementing count for existing notification');
        return {
          ...prev,
          count: prev.count + 1,
          timestamp: now
        };
      } else {
        // New message - reset count
        console.log('[NOTIFICATION] Creating new success notification');
        return {
          message,
          count: 1,
          timestamp: now,
          id
        };
      }
    });
    
    // Clear error notification if showing success
    setErrorState(null);
    
    // Set auto-hide timeout
    console.log('[NOTIFICATION] Setting timeout for', duration, 'ms');
    const timeout = setTimeout(() => {
      console.log('[NOTIFICATION] Timeout triggered for success notification:', id);
      setSuccessState(prev => {
        if (prev?.id === id) {
          console.log('[NOTIFICATION] Clearing success notification');
          notificationTimeouts.current.delete(id);
          return null;
        }
        console.log('[NOTIFICATION] Success notification ID mismatch, keeping:', prev?.id);
        return prev;
      });
    }, duration);
    
    notificationTimeouts.current.set(id, timeout);
  }, []);

  const clearNotifications = useCallback(() => {
    // Clear all timeouts
    notificationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    notificationTimeouts.current.clear();
    
    // Clear states
    setErrorState(null);
    setSuccessState(null);
  }, []);

  // Add CSS keyframes for animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInUp {
        from {
          transform: translateX(-50%) translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Simplified loadCartonContents - just update local state if needed
  const loadCartonContents = useCallback(async (cartonCode: string) => {
    // Update scanned carton reference
    setScannedCarton(cartonCode);
    return true;
  }, []);

  const updateStats = useCallback((stats: {
    totalPanels: number;
    receivedPanels: number;
    totalHardware: number;
    receivedHardware: number;
    pendingItems: number;
  }) => {
    if (updateCartonStats) {
      updateCartonStats(stats);
    }
  }, [updateCartonStats]);

  // Update carton stats when effective carton contents change
  useEffect(() => {
    const stats = {
      totalPanels: effectiveCartonContents.panels.length,
      receivedPanels: effectiveCartonContents.panels.filter((p: any) => p.is_received).length,
      totalHardware: effectiveCartonContents.hardware.reduce((sum, hw) => sum + (hw.quantity || 1), 0),
      receivedHardware: effectiveCartonContents.hardware.reduce((sum, hw) => 
        sum + (hw.is_received ? (hw.quantity || 1) : 0), 0),
      pendingItems: 0
    };
    
    stats.pendingItems = (stats.totalPanels - stats.receivedPanels) + 
                         (stats.totalHardware - stats.receivedHardware);
    
    // Update local carton stats for display
    setCartonStats({
      panel: {
        total: stats.totalPanels,
        received: stats.receivedPanels,
        notReceived: stats.totalPanels - stats.receivedPanels
      },
      hardware: {
        total: stats.totalHardware,
        received: stats.receivedHardware,
        notReceived: stats.totalHardware - stats.receivedHardware
      }
    });
    
    updateStats(stats);
  }, [effectiveCartonContents, updateStats]);

  const handleScanSuccess = useCallback(async (result: string) => {
    console.log('[SCANNER] Scan success:', result);
    
    if (scanCooldown) {
      console.log('[SCANNER] Scan cooldown active, ignoring scan');
      return;
    }

    if (!result) {
      console.log('[SCANNER] Empty scan result');
      return;
    }

    // Check if this is the same code as the last scan (within 2 seconds)
    const now = Date.now();
    if (lastScannedCode === result && (now - (scannedCodes[scannedCodes.length - 1]?.timestamp || 0)) < 2000) {
      console.log('[SCANNER] Duplicate scan detected, ignoring');
      return;
    }

    setScanCooldown(true);

    try {
      // First, check if we're in a state where we can process this scan
      if (!currentProject) {
        console.log('[SCANNER] No project selected');
        return;
      }

      // Additional validation - reject common noise patterns
      const trimmedResult = result.trim();
      if (trimmedResult.length < 3 || 
          trimmedResult === 'undefined' || 
          trimmedResult === 'null' ||
          trimmedResult.match(/^[0-9]+$/) && trimmedResult.length < 5) {
        console.log('[SCANNER] Rejecting invalid scan result:', trimmedResult);
        return;
      }

      // Update scanned codes tracking
      setScannedCodes(prev => [...prev, { code: trimmedResult, timestamp: now }]);
      setLastScannedCode(trimmedResult);

      // Forward the scan result to the parent component
      console.log('[SCANNER] Forwarding scan to parent:', trimmedResult);
      onScan(trimmedResult);
      
      // Show success message for valid scans
      showSuccessNotification(`Scanned: ${trimmedResult}`, 2000);

    } catch (error) {
      console.error('[SCANNER] Error processing scan:', error);
    } finally {
      // Reset the cooldown after a short delay
      setTimeout(() => {
        setScanCooldown(false);
      }, 1000);
    }
  }, [currentProject, onScan, scanCooldown, lastScannedCode, scannedCodes]);

  const handleConfirmSwitch = useCallback(async () => {
    if (pendingCarton) {
      setScannedCarton(pendingCarton);
      await loadCartonContents(pendingCarton);
      setShowSwitchConfirm(false);
      setPendingCarton(null);
    }
  }, [pendingCarton, loadCartonContents]);

  const handleCancelSwitch = useCallback(() => {
    setShowSwitchConfirm(false);
    setPendingCarton(null);
  }, []);


  // Stop scanning and clean up
  const stopScanning = useCallback(() => {
    console.log(' Stopping scanner...');

    if (readerRef.current) {
      try {
        if ((readerRef.current as any).controls) {
          (readerRef.current as any).controls.stop();
          (readerRef.current as any).controls = null;
        }

        if (typeof (readerRef.current as any)?.reset === 'function') {
          (readerRef.current as any).reset();
        }
        readerRef.current = null;
      } catch (error) {
        console.warn('Error stopping reader:', error);
      }
    }

    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        streamRef.current = null;
      } catch (error) {
        console.warn('Error stopping stream:', error);
      }
    }

    if (videoRef.current) {
      try {
        // Pause the video first
        videoRef.current.pause();
        // Clear the source
        videoRef.current.srcObject = null;
        // Reset video properties
        videoRef.current.load();
      } catch (error) {
        console.warn('Error clearing video:', error);
      }
    }

    setIsScanning(false);
    setIsReaderActive(false);
    setTorchEnabled(false);
    setScannedCodes([]);
    setLastScannedCode('');
    setScanCooldown(false);
  }, []);

  // Initialize the scanner
  useEffect(() => {
    const hints = new Map<DecodeHintType, any>();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODABAR,
      BarcodeFormat.ITF,
      BarcodeFormat.DATA_MATRIX
    ]);

    readerRef.current = new BrowserMultiFormatReader(hints);

    // Get available cameras
    const getCameras = async () => {
      try {
        // First request camera access
        await navigator.mediaDevices.getUserMedia({ video: true });
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        // Try to find rear camera first
        const rearCamera = videoDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        );

        setCameras(videoDevices);
        
        // Set rear camera if available, otherwise use first camera
        if (rearCamera) {
          setSelectedCamera(rearCamera);
        } else if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0]);
        }

        // Check torch support
        if (videoDevices.length > 0) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: (rearCamera || videoDevices[0]).deviceId }
          });
          const track = stream.getVideoTracks()[0];
          const capabilities = track.getCapabilities ? track.getCapabilities() : {};
          const hasTorch = 'torch' in capabilities || 'torch' in track.getSettings();
          setIsTorchSupported(hasTorch);
          
          // Cleanup
          track.stop();
          stream.getTracks().forEach(t => t.stop());
        }
      } catch (error) {
        console.error('Error initializing camera:', error);
        showErrorNotification(
          'Failed to access camera. Please check permissions.',
          5000
        );
      }
    };

    getCameras();

    return () => {
      stopScanning();
      clearNotifications();
    };
  }, [clearNotifications, stopScanning]);

  // Start/stop scanning when selected camera changes
  useEffect(() => {
    if (selectedCamera) {
      startScanning(selectedCamera.deviceId);
    }

    // Cleanup function - only run when component unmounts or selectedCamera changes
    return () => {
      if (selectedCamera) {
        stopScanning();
      }
    };
  }, [selectedCamera]);

  const startScanning = useCallback(async (deviceId: string) => {
    try {
      if (!videoRef.current) return;
      
      // Prevent multiple readers from starting
      if (isReaderActive) {
        console.log('[SCANNER] Reader already active, skipping start');
        return;
      }
      
      // Stop any existing scanning first
      if (readerRef.current) {
        try {
          if ((readerRef.current as any).controls) {
            (readerRef.current as any).controls.stop();
            (readerRef.current as any).controls = null;
          }
          if (typeof (readerRef.current as any)?.reset === 'function') {
            (readerRef.current as any).reset();
          }
        } catch (error) {
          console.warn('Error stopping existing reader:', error);
        }
      }
      
      // Add a small delay to prevent rapid start/stop cycles
      await new Promise(resolve => setTimeout(resolve, 100));

      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.CODE_128,
        BarcodeFormat.QR_CODE,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.CODABAR,
        BarcodeFormat.ITF,
        BarcodeFormat.RSS_14,
        BarcodeFormat.RSS_EXPANDED,
      ]);

      const reader = new BrowserMultiFormatReader(hints);
      readerRef.current = reader;

      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          facingMode: 'environment', // Prefer rear camera
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      // Get the stream with the specified constraints
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Update torch support status
      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities ? videoTrack.getCapabilities() : {};
      const hasTorch = 'torch' in capabilities || 'torch' in videoTrack.getSettings();
      setIsTorchSupported(hasTorch);

      // If we have a previous stream, stop it
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      
      // Wait for video to be ready and handle play() interruption
      try {
        // Ensure video is loaded before playing
        if (videoRef.current.readyState < 2) {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Video load timeout')), 5000);
            videoRef.current!.addEventListener('loadeddata', () => {
              clearTimeout(timeout);
              resolve(true);
            }, { once: true });
            videoRef.current!.addEventListener('error', () => {
              clearTimeout(timeout);
              reject(new Error('Video load error'));
            }, { once: true });
          });
        }
        
        // Play the video with proper error handling
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
      } catch (error) {
        console.warn('Video play interrupted or failed:', error);
        // If play was interrupted, try again after a short delay
        if (error instanceof Error && error.name === 'NotAllowedError') {
          throw new Error('Camera access denied. Please allow camera permissions.');
        } else if (error instanceof Error && error.message.includes('interrupted')) {
          // Retry after a short delay
          await new Promise(resolve => setTimeout(resolve, 100));
          try {
            await videoRef.current.play();
          } catch (retryError) {
            console.warn('Video play retry failed:', retryError);
            throw new Error('Failed to start camera. Please try again.');
          }
        } else {
          throw error;
        }
      }

      // Start the barcode scanner
      readerRef.current.decodeFromVideoDevice(
        deviceId || null,
        videoRef.current,
        (result, error) => {
          if (result) {
            const scannedText = result.getText();
            console.log('[SCANNER] Raw scan result:', scannedText);
            
            // Validate the scanned text - reject empty or invalid results
            if (!scannedText || scannedText.trim().length === 0) {
              console.log('[SCANNER] Rejecting empty scan result');
              return;
            }
            
            // Reject very short results that are likely noise
            if (scannedText.trim().length < 3) {
              console.log('[SCANNER] Rejecting short scan result:', scannedText);
              return;
            }
            
            handleScanSuccess(scannedText);
          } else if (error && !(error instanceof Error && error.name === 'NotFoundException')) {
            console.warn('Scan error:', error);
          }
        }
      );

      console.log('[SCANNER] Barcode reader started successfully');

      setIsScanning(true);
      setIsReaderActive(true);
    } catch (error) {
      console.error('Error starting scanner:', error);
      showErrorNotification(
        error instanceof Error ? error.message : 'Failed to start scanner', 
        5000
      );
    }
  }, [handleScanSuccess, isReaderActive]);

  const toggleTorch = useCallback(async () => {
    if (!isTorchSupported || !streamRef.current) return;

    try {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      const constraints: MediaTrackConstraints = {
        advanced: [{ torch: !torchEnabled } as any]
      };
      
      await videoTrack.applyConstraints(constraints);
      
      // Check if torch was actually toggled
      const settings = videoTrack.getSettings();
      const newTorchState = settings.torch || false;
      
      setTorchEnabled(newTorchState);
      
      // If the torch state didn't change as requested, try the ImageCapture API as fallback
      if (newTorchState === torchEnabled) {
        try {
          const imageCapture = new (window as any).ImageCapture(videoTrack);
          await imageCapture.setOptions({
            fillLightMode: newTorchState ? 'flash' : 'off'
          });
          setTorchEnabled(!torchEnabled);
        } catch (err) {
          console.warn('Could not use ImageCapture API for torch:', err);
        }
      }
    } catch (error) {
      console.error('Error toggling torch:', error);
      showErrorNotification(
        'Failed to toggle torch. Your device may not support this feature.', 
        4000
      );
    }
  }, [isTorchSupported, torchEnabled]);

  const handleManualSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const barcode = manualBarcode.trim();
    if (!barcode) return;

    try {
      // Show scanning feedback
      setCurrentScanText(`Processing: ${barcode}`);
      
      // Simulate scan delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Process the barcode
      await handleScanSuccess(barcode);
      
      // Show success feedback
      showSuccessNotification(`Scanned: ${barcode}`, 2000);
      
      // Reset form
      setManualBarcode('');
      setShowManualInput(false);
      
    } catch (error) {
      console.error('Error processing manual input:', error);
      showErrorNotification(
        error instanceof Error ? error.message : 'Failed to process barcode',
        4000
      );
    } finally {
      // Clear scan text after a delay
      setTimeout(() => setCurrentScanText(''), 2000);
    }
  }, [manualBarcode, handleScanSuccess]);

  const toggleInfoPanel = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setShowInfo(prev => !prev);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInfoButton = target.closest('button[title="Scanner info"]');
      const isInsidePanel = target.closest('[data-info-panel]');

      if (showInfo && !isInfoButton && !isInsidePanel) {
        setShowInfo(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInfo]);

  useEffect(() => {
    if (!errorState) return;
    const timer = setTimeout(() => setErrorState(null), 3000);
    return () => clearTimeout(timer);
  }, [errorState]);

  useEffect(() => {
    if (!successState) return;
    const timer = setTimeout(() => setSuccessState(null), 2000);
    return () => clearTimeout(timer);
  }, [successState]);

  const styles = {
    container: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'black',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
    } as CSSProperties,
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      padding: '8px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 1002,
      background: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
      height: '56px',
      boxSizing: 'border-box',
    } as CSSProperties,
    projectBanner: {
      position: 'fixed',
      top: '56px', // Below header
      left: 0,
      right: 0,
      padding: '8px 12px',
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      color: 'white',
      zIndex: 1001,
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      fontSize: '13px',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
      boxSizing: 'border-box',
    } as CSSProperties,
    projectInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      flex: '1 1 180px',
      minWidth: 0, // Allow text truncation
    } as CSSProperties,
    projectInfoRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as CSSProperties,
    projectLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      minWidth: 0, // Allow text truncation
    } as CSSProperties,
    projectText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as CSSProperties,
    cartonInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 0, // Allow text truncation
      textAlign: 'right',
    } as CSSProperties,
    statRow: {
      display: 'flex',
      justifyContent: 'end',
      gap: '8px',
    } as CSSProperties,
    statItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '50px',
    } as CSSProperties,
    statLabel: {
      fontSize: '10px',
      opacity: 0.8,
      whiteSpace: 'nowrap',
    } as CSSProperties,
    statValue: {
      fontWeight: 'bold',
      fontSize: '9px',
    } as CSSProperties,
    controls: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
    } as CSSProperties,
    button: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: 'white',
      transition: 'all 0.2s ease',
    } as CSSProperties,
    videoContainer: {
      position: 'absolute',
      top: '110px', // Below header + banner
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    } as CSSProperties,
    video: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    } as CSSProperties,
    scanFrame: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '250px',
      height: '250px',
      border: `2px solid ${qrDetected ? '#4CAF50' : '#00ff00'}`,
      borderRadius: '8px',
      pointerEvents: 'none',
      transition: 'all 0.3s ease',
    } as CSSProperties,
    cameraSelect: {
      position: 'absolute',
      top: '100%',
      right: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderRadius: '8px',
      padding: '0.5rem',
      marginTop: '0.5rem',
      zIndex: 30,
      maxHeight: '200px',
      overflowY: 'auto',
    } as CSSProperties,
    cameraOption: {
      padding: '0.5rem',
      color: 'white',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '200px',
    } as CSSProperties,
    infoPanel: {
      position: 'absolute',
      top: '70px',
      right: '10px',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderRadius: '8px',
      padding: '1rem',
      color: 'white',
      zIndex: 30,
      width: '250px',
      maxHeight: '70vh',
      overflowY: 'auto',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    } as CSSProperties,
    scanTextContainer: {
      position: 'absolute',
      top: '70px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 25,
      width: '90%',
      maxWidth: '500px',
      pointerEvents: 'none',
    } as CSSProperties,
    scanText: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: 'white',
      padding: '10px 20px',
      borderRadius: '20px',
      fontSize: '16px',
      textAlign: 'center',
      wordBreak: 'break-word',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
      margin: '0 auto',
      maxWidth: '100%',
    } as CSSProperties,
    manualInput: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '8px',
      zIndex: 100,
      width: '90%',
      maxWidth: '400px',
    } as CSSProperties,
    input: {
      width: '100%',
      padding: '0.75rem',
      margin: '1rem 0',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
    } as CSSProperties,
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    } as CSSProperties,
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem',
    } as CSSProperties,
    confirmDialog: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000,
    } as CSSProperties,
    confirmDialogContent: {
      backgroundColor: '#1e293b',
      padding: '20px',
      borderRadius: '8px',
      maxWidth: '400px',
      width: '90%',
      color: 'white',
    } as CSSProperties,
    confirmDialogButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '20px',
    } as CSSProperties,
    cancelButton: {
      padding: '8px 16px',
      backgroundColor: '#334155',
      border: 'none',
      borderRadius: '4px',
      color: 'white',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#1f2937',
      },
    } as CSSProperties,
    confirmButton: {
      padding: '8px 16px',
      backgroundColor: '#3b82f6',
      border: 'none',
      borderRadius: '4px',
      color: 'white',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#2563eb',
      },
    } as CSSProperties,
    contentsPanel: {
      position: 'fixed',
      top: '150px',
      left: '20px',
      right: '20px',
      maxHeight: '50vh',
      backgroundColor: 'rgba(30, 41, 59, 0.85)',
      borderRadius: '8px',
      overflow: 'hidden',
      zIndex: 1000,
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    } as CSSProperties,
    contentsHeader: {
      padding: '12px 16px',
      backgroundColor: 'rgba(30, 41, 59, 0.9)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      color: 'white',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    } as CSSProperties,
    contentsList: {
      maxHeight: 'calc(50vh - 50px)',
      overflowY: 'auto',
      padding: '12px',
    } as CSSProperties,
    contentsSection: {
      marginBottom: '16px',
    } as CSSProperties,
    contentsItems: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginTop: '8px',
    } as CSSProperties,
    contentsItem: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      padding: '6px 12px',
      borderRadius: '4px',
      fontSize: '14px',
      color: 'white',
      transition: 'all 0.2s ease',
      borderLeft: '3px solid #3b82f6',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
      },
    } as CSSProperties,
    notification: {
      position: 'fixed',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '12px 20px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      zIndex: 2000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      maxWidth: '90%',
      minWidth: '280px',
      textAlign: 'left',
      transition: 'all 0.3s ease',
      fontSize: '15px',
      lineHeight: '1.4',
      animation: 'slideInUp 0.3s ease-out',
    } as CSSProperties,
    errorNotification: {
      bottom: '100px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: '1px solid #dc2626',
    } as CSSProperties,
    successNotification: {
      bottom: '80px',
      backgroundColor: '#10b981',
      color: 'white',
      border: '1px solid #059669',
    } as CSSProperties,
  };

  const handleCameraSwitch = useCallback(async () => {
    if (cameras.length < 2) return;

    try {
      const currentIndex = cameras.findIndex(cam => cam.deviceId === selectedCamera?.deviceId);
      const nextIndex = (currentIndex + 1) % cameras.length;
      const nextCamera = cameras[nextIndex];

      setSelectedCamera(nextCamera);
      await stopScanning();
      await startScanning(nextCamera.deviceId);
      
      // Update camera name in the UI
      const cameraName = nextCamera.label || 
        (nextCamera.groupId.includes('back') || nextCamera.groupId.includes('rear') ? 'Rear Camera' : 'Front Camera');
      
      showSuccessNotification(`Switched to ${cameraName}`, 2000);
    } catch (error) {
      console.error('Error switching camera:', error);
      showErrorNotification('Failed to switch camera', 4000);
    }
  }, [cameras, selectedCamera, startScanning, stopScanning, showSuccessNotification, showErrorNotification]);

  const renderProjectBanner = useCallback(() => {
    console.log(currentProject);
    if (!currentProject) return null;

    // Handle both detailed and basic project structures
    const projectData = (currentProject as any).project || currentProject;
    const projectName = projectData.display_name || projectData.name;
    const customerName = projectData.customer_name || 'No customer';
    const soNumber = projectData.so_number || 'N/A';

    // Use current carton from props if available, otherwise use scanned carton
    const displayCarton = currentCarton || scannedCarton;
    const displayCartonCode = typeof displayCarton === 'string' ? displayCarton : displayCarton?.code;

    return (
      <div style={styles.projectBanner}>
        <div style={styles.projectInfo}>
          <div style={styles.projectInfoRow}>
            <Package size={12} />
            <span>{projectName || 'No project name'}</span>
          </div>
          <div style={styles.projectInfoRow}>
            <User size={12} />
            <span>{customerName}</span>
          </div>
          <div style={styles.projectInfoRow}>
            <FileText size={12} />
            <span>SO: {soNumber}</span>
          </div>
        </div>
        {displayCartonCode && (
          <div style={styles.cartonInfo}>
            <div>Carton: {displayCartonCode}</div>
            <div style={styles.statRow}>
              <div style={styles.statItem}>
                <div style={styles.statLabel}>Panel</div>
                <div style={styles.statValue}>
                  {cartonStats.panel.received} / {cartonStats.panel.total}
                </div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statLabel}>Hardware</div>
                <div style={styles.statValue}>
                  {cartonStats.hardware.received} / {cartonStats.hardware.total}
                </div>
              </div>
            </div>
            <div style={{ ...styles.statRow, fontSize: '10px', opacity: 0.7, justifyContent: 'flex-end' }}>
              <span>Pending: {cartonStats.panel.notReceived}P, {cartonStats.hardware.notReceived}H</span>
            </div>
          </div>
        )}
      </div>
    );
  }, [currentProject, currentCarton, scannedCarton, cartonStats]);


  useEffect(() => {
    // Effect implementation
    return () => {
      // Cleanup if needed
    };
  }, []);

  if (requireProject && !currentProject) {
    console.log('Showing no project screen');
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        zIndex: 1000,
        padding: '20px',
        textAlign: 'center',
      }}>
        <AlertTriangle size={48} style={{ marginBottom: '20px' }} />
        <h2>No Project Selected</h2>
        <p style={{ marginBottom: '20px' }}>Please select a project before scanning.</p>
        <button
          onClick={onClose}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          Close Scanner
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header with Controls */}
      <div style={styles.header}>
        <h2 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>Scan</h2>
        <div style={styles.controls}>
          {/* Camera Switch Button */}
          <button
            onClick={handleCameraSwitch}
            style={{
              ...styles.button,
              opacity: cameras.length > 1 ? 1 : 0.5,
              cursor: cameras.length > 1 ? 'pointer' : 'not-allowed',
            }}
            title={cameras.length > 1 ? "Switch Camera" : "Only one camera available"}
          >
            <Camera size={20} />
          </button>

          {/* Torch Button */}
          <button
            onClick={toggleTorch}
            style={{
              ...styles.button,
              opacity: isTorchSupported ? 1 : 0.5,
              cursor: isTorchSupported ? 'pointer' : 'not-allowed',
              color: torchEnabled ? '#ffd700' : 'white',
            }}
            title={isTorchSupported ? (torchEnabled ? 'Turn off torch' : 'Turn on torch') : 'Torch not supported'}
          >
            {torchEnabled ? <Zap size={20} fill="#ffd700" /> : <Zap size={20} />}
          </button>

          {/* Manual Input Button */}
          <button
            onClick={() => setShowManualInput(true)}
            style={styles.button}
            title="Manual Input"
          >
            <Keyboard size={20} />
          </button>

          {/* Camera Info Button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={toggleInfoPanel}
              style={{
                ...styles.button,
                background: showInfo ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              }}
              title={`${selectedCamera?.label || 'Camera Info'}`}
            >
              <Info size={20} />
            </button>
            {showInfo && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                right: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '12px',
                borderRadius: '8px',
                width: '250px',
                zIndex: 1000,
                marginBottom: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <h4 style={{ margin: '0 0 8px 0' }}>Camera Information</h4>
                <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  <div><strong>Camera:</strong> {selectedCamera?.label || 'Unknown'}</div>
                  <div><strong>Status:</strong> {isScanning ? 'Scanning' : 'Paused'}</div>
                  <div><strong>Torch:</strong> {isTorchSupported ? (torchEnabled ? 'On' : 'Off') : 'Not Supported'}</div>
                  <div><strong>Available Cameras:</strong> {cameras.length}</div>
                  <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>
                    Scan barcodes or QR codes. Make sure the code is well-lit and within the frame.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              ...styles.button,
              background: 'rgba(255, 59, 48, 0.8)',
            }}
            title="Close Scanner"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Project Banner */}
      {currentProject && renderProjectBanner()}

      {/* Video Preview */}
      <div style={styles.videoContainer}>
        <video
          ref={videoRef}
          style={styles.video}
          playsInline
          muted
          autoPlay
        />
        {showScanFrame && <div style={styles.scanFrame} />}
      </div>

      {/* Rest of the UI */}
      {showInfo && (
        <div style={styles.infoPanel}>
          <h3>Scanner Information</h3>
          <p>Scan barcodes or QR codes to process items.</p>
          <p>Make sure the barcode is within the frame and well-lit.</p>
        </div>
      )}

      {showManualInput && (
        <div style={styles.manualInput}>
          <h3>Enter Barcode Manually</h3>
          <input
            type="text"
            value={manualBarcode}
            onChange={(e) => setManualBarcode(e.target.value)}
            style={styles.input}
            placeholder="Enter barcode"
            autoFocus
          />
          <div style={styles.buttonGroup}>
            <button
              onClick={() => setShowManualInput(false)}
              style={{
                ...styles.actionButton,
                backgroundColor: '#e2e8f0',
                color: '#1e293b',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleManualSubmit}
              style={{
                ...styles.actionButton,
                backgroundColor: '#3b82f6',
                color: 'white',
              }}
              disabled={!manualBarcode.trim()}
            >
              Submit
            </button>
          </div>
        </div>
      )}

      {showSwitchConfirm && (
        <div style={styles.confirmDialog}>
          <div style={styles.confirmDialogContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <PackageX size={24} />
              <h3>Switch Carton?</h3>
            </div>
            <p>
              You have {effectiveCartonContents.panels.filter(p => !p.is_received).length} pending panels and{' '}
              {effectiveCartonContents.hardware.filter(hw => !hw.is_received).length} pending hardware items.
              <br />
              Are you sure you want to switch to a different carton?
            </p>
            <div style={styles.confirmDialogButtons}>
              <button
                onClick={handleCancelSwitch}
                style={styles.cancelButton}
              >
                <X size={16} style={{ marginRight: '4px' }} />
                Cancel
              </button>
              <button
                onClick={handleConfirmSwitch}
                style={styles.confirmButton}
              >
                <PackageCheck size={16} style={{ marginRight: '4px' }} />
                Switch Carton
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carton Contents Panel */}
      {(currentCarton || scannedCarton) && (
        <div style={styles.contentsPanel}>
          <div
            style={styles.contentsHeader}
            onClick={() => setShowContents(!showContents)}
          >
            <div style={{ display: 'flex',color:'white', alignItems: 'center', gap: '8px' }}>
              <Package size={18} />
              <h4>Carton: {currentCarton?.code || scannedCarton}</h4>
            </div>
            <div style={{ display: 'flex',color:'white', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', opacity: 0.8 }}>
                {showContents ? 'Hide' : 'Show'} contents
              </span>
              {showContents ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
          </div>
          {showContents && (
            <div style={styles.contentsList}>
              <div style={styles.contentsSection}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Box size={16} />
                  <h5>Panels ({effectiveCartonContents.panels.length})</h5>
                </div>
                <div style={styles.contentsItems}>
                  {effectiveCartonContents.panels.length > 0 ? (
                    effectiveCartonContents.panels.map(panel => (
                      <div
                        key={panel.id}
                        style={{
                          ...styles.contentsItem,
                          opacity: panel.is_received ? 0.6 : 1,
                          borderLeft: panel.is_received ? '3px solid #10b981' : '3px solid #3b82f6',
                          backgroundColor: panel.is_received ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)'
                        }}
                      >
                        <span style={{ marginRight: '4px' }}>
                          {panel.is_received ? <Check size={14} /> : <Box size={14} />}
                        </span>
                        {panel.code}
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#9ca3af', fontSize: '14px', padding: '8px 0' }}>
                      No panels in this carton
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.contentsSection}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Boxes size={16} />
                  <h5>Hardware ({effectiveCartonContents.hardware.reduce((sum, hw) => sum + (hw.quantity || 1), 0)})</h5>
                </div>
                <div style={styles.contentsItems}>
                  {effectiveCartonContents.hardware.length > 0 ? (
                    effectiveCartonContents.hardware.map(hw => (
                      <div
                        key={hw.id}
                        style={{
                          ...styles.contentsItem,
                          opacity: hw.is_received ? 0.6 : 1,
                          borderLeft: hw.is_received ? '3px solid #10b981' : '3px solid #8b5cf6',
                          backgroundColor: hw.is_received ? 'rgba(16, 185, 129, 0.1)' : 'rgba(139, 92, 246, 0.1)'
                        }}
                      >
                        <span style={{ marginRight: '4px' }}>
                          {hw.is_received ? <Check size={14} /> : <Boxes size={14} />}
                        </span>
                        {(hw as any).code || (hw as any).group_code} (x{hw.quantity || 1})
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#9ca3af', fontSize: '14px', padding: '8px 0' }}>
                      No hardware in this carton
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dismiss All Button */}
      {(errorState || successState) && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 3000,
        }}>
          <button
            onClick={clearNotifications}
            style={{
              background: 'rgba(0, 0, 0, 0.7)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
            }}
            title="Dismiss all notifications"
          >
            <X size={16} />
            Dismiss All
          </button>
        </div>
      )}

      {/* Notifications */}
      {errorState && (
        <div style={{
          ...styles.notification,
          ...styles.errorNotification,
          bottom: successState ? '140px' : '100px',
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
              Error
              {errorState.count > 1 && (
                <span style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {errorState.count}
                </span>
              )}
            </div>
            <div>{errorState.message}</div>
          </div>
          <button
            onClick={() => {
              // Clear timeout for this notification
              const timeout = notificationTimeouts.current.get(`error_${errorState.message}`);
              if (timeout) {
                clearTimeout(timeout);
                notificationTimeouts.current.delete(`error_${errorState.message}`);
              }
              setErrorState(null);
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              minWidth: '28px',
              minHeight: '28px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {successState && (
        <div style={{
          ...styles.notification,
          ...styles.successNotification,
        }}>
          <Check size={18} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
              Success
              {successState.count > 1 && (
                <span style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {successState.count}
                </span>
              )}
            </div>
            <div>{successState.message}</div>
          </div>
          <button
            onClick={() => {
              // Clear timeout for this notification
              const timeout = notificationTimeouts.current.get(`success_${successState.message}`);
              if (timeout) {
                clearTimeout(timeout);
                notificationTimeouts.current.delete(`success_${successState.message}`);
              }
              setSuccessState(null);
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              minWidth: '28px',
              minHeight: '28px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Scanner;
